import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

function DashboardEnhanced({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [cmsStats, setCmsStats] = useState(null);
  const [cmsLoading, setCmsLoading] = useState(false);
  const [cmsError, setCmsError] = useState('');
  const [loading, setLoading] = useState(true);
  const canAccessCms = user?.role === 'Admin' || user?.role === 'SuperAdmin';

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    if (canAccessCms) fetchCmsStats();
  }, [canAccessCms]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCmsStats = async () => {
    try {
      setCmsLoading(true);
      setCmsError('');
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/cms/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCmsStats(response.data.counts || {});
    } catch (error) {
      console.error('Failed to fetch CMS stats:', error);
      setCmsError('CMS overview could not be loaded');
    } finally {
      setCmsLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, bgColor, icon, link }) => {
    const CardContent = (
      <div
        className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 h-full"
        style={{ borderTop: `3px solid ${bgColor}` }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-600">{title}</h3>
          <span
            className="text-xl w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${bgColor}18`, color: bgColor }}
          >
            {icon}
          </span>
        </div>
        <p className="text-3xl font-bold mb-1 text-gray-900">{value ?? 0}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    );

    return link ? <Link className="block h-full" to={link}>{CardContent}</Link> : <div className="h-full">{CardContent}</div>;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6" data-testid="dashboard-page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Comprehensive Dashboard</h1>
            <p className="text-gray-600 mt-1">All Modules Overview</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Welcome, {user?.role || 'User'}</p>
            <p className="text-xs text-gray-500">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center py-10">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        ) : stats ? (
          <div>
            {/* Module 1 & 2: Employee Management & Departments */}
            <div className="mb-6">
              <h2 className="text-lg font-bold mb-3 flex items-center" style={{color: '#2b3380'}}>
                <span className="mr-2">👥</span> Employee Management
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                  title="Total Employees" 
                  value={stats.total_employees} 
                  subtitle="Active employees"
                  bgColor="#2b3380" 
                  icon="👤"
                  link="/employees"
                />
                <StatCard 
                  title="Departments" 
                  value={stats.total_departments} 
                  subtitle="Active departments"
                  bgColor="#7e57c2" 
                  icon="🏢"
                  link="/departments"
                />
                <StatCard 
                  title="NAPS Active" 
                  value={stats.naps_active} 
                  subtitle="Apprenticeship scheme"
                  bgColor="#00897b" 
                  icon="📋"
                />
                <StatCard 
                  title="NATS Active" 
                  value={stats.nats_active} 
                  subtitle="Training scheme"
                  bgColor="#00acc1" 
                  icon="🎓"
                />
              </div>
            </div>

            {/* Module 3: Attendance */}
            <div className="mb-6">
              <h2 className="text-lg font-bold mb-3 flex items-center" style={{color: '#4caf50'}}>
                <span className="mr-2">📅</span> Attendance
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                  title="Present Today" 
                  value={stats.present_today} 
                  subtitle={`${stats.attendance_percentage}% attendance rate`}
                  bgColor="#4caf50" 
                  icon="✅"
                  link="/attendance"
                />
                <StatCard 
                  title="Absent Today" 
                  value={stats.absent_today} 
                  subtitle="Requires attention"
                  bgColor="#f44336" 
                  icon="❌"
                />
                <StatCard 
                  title="On Leave Today" 
                  value={stats.on_leave_today} 
                  subtitle="Approved leaves"
                  bgColor="#ff9800" 
                  icon="🏖️"
                />
                <StatCard 
                  title="Total Shifts" 
                  value={stats.total_shifts} 
                  subtitle="Configured shifts"
                  bgColor="#607d8b" 
                  icon="⏰"
                  link="/shifts"
                />
              </div>
            </div>

            {/* Module 4 & 5: Leave & Payroll */}
            <div className="mb-6">
              <h2 className="text-lg font-bold mb-3 flex items-center" style={{color: '#ff9800'}}>
                <span className="mr-2">💼</span> Leave & Payroll Management
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                  title="Pending Leaves" 
                  value={stats.pending_leaves} 
                  subtitle="Awaiting approval"
                  bgColor="#ff9800" 
                  icon="📝"
                  link="/leaves"
                />
                <StatCard 
                  title="Pending Payroll" 
                  value={stats.pending_payroll} 
                  subtitle="To be processed"
                  bgColor="#9c27b0" 
                  icon="💰"
                  link="/payroll"
                />
                <StatCard 
                  title="Payroll Amount" 
                  value={`₹${(stats.total_payroll_amount / 100000).toFixed(1)}L`}
                  subtitle="Current month total"
                  bgColor="#673ab7" 
                  icon="💵"
                />
                <StatCard 
                  title="Upcoming Holidays" 
                  value={stats.upcoming_holidays} 
                  subtitle="Next 30 days"
                  bgColor="#03a9f4" 
                  icon="🎉"
                  link="/holidays"
                />
              </div>
            </div>

            {/* Module 6-9: Performance, Documents, Announcements, Onboarding */}
            <div className="mb-6">
              <h2 className="text-lg font-bold mb-3 flex items-center" style={{color: '#e91e63'}}>
                <span className="mr-2">⭐</span> Operations & HR
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                  title="Pending Reviews" 
                  value={stats.pending_reviews} 
                  subtitle={`${stats.completed_reviews} completed`}
                  bgColor="#e91e63" 
                  icon="📊"
                  link="/performance"
                />
                <StatCard 
                  title="Pending Documents" 
                  value={stats.pending_documents} 
                  subtitle={`${stats.total_documents} total docs`}
                  bgColor="#ff5722" 
                  icon="📄"
                  link="/documents"
                />
                <StatCard 
                  title="Active Announcements" 
                  value={stats.active_announcements} 
                  subtitle="Last 30 days"
                  bgColor="#3f51b5" 
                  icon="📢"
                  link="/announcements"
                />
                <StatCard 
                  title="Onboarding Progress" 
                  value={stats.pending_onboarding} 
                  subtitle={`${stats.completed_onboarding} completed`}
                  bgColor="#009688" 
                  icon="🎯"
                  link="/onboarding"
                />
              </div>
            </div>

            {/* Website CMS Overview */}
            {canAccessCms && (
              <div className="mb-6" data-testid="cms-overview">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h2 className="text-lg font-bold flex items-center" style={{color: '#0f766e'}}>
                    <span className="mr-2">🌐</span> Website CMS Overview
                  </h2>
                  <Link to="/cms" className="text-sm font-semibold text-[#2b3380] hover:underline">Manage Website CMS →</Link>
                </div>
                {cmsError ? (
                  <div className="bg-white border border-red-200 rounded-xl p-5 flex flex-wrap items-center justify-between gap-3">
                    <span className="text-sm text-red-700">{cmsError}</span>
                    <button type="button" onClick={fetchCmsStats} className="text-sm font-semibold text-[#2b3380]">Retry</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard title="Website Pages" value={cmsLoading ? '—' : cmsStats?.pages?.total} subtitle={`${cmsStats?.pages?.published ?? 0} published`} bgColor="#4f46e5" icon="📄" link="/cms" />
                    <StatCard title="Website Products" value={cmsLoading ? '—' : cmsStats?.products?.total} subtitle={`${cmsStats?.products?.active ?? 0} active`} bgColor="#0f766e" icon="📱" link="/cms" />
                    <StatCard title="Banners & Sliders" value={cmsLoading ? '—' : cmsStats?.banners?.total} subtitle={`${cmsStats?.banners?.published ?? 0} published`} bgColor="#d97706" icon="🖼️" link="/cms" />
                    <StatCard title="Contact Enquiries" value={cmsLoading ? '—' : cmsStats?.enquiries?.total} subtitle="Website submissions" bgColor="#be185d" icon="📨" link="/cms" />
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>Quick Actions</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-7 gap-3">
                <Link
                  to="/employees/new"
                  className="text-white py-3 px-4 rounded-lg text-center text-sm font-medium hover:opacity-90 transition"
                  style={{backgroundColor: '#2b3380'}}
                >
                  ➕ Add Employee
                </Link>
                <Link
                  to="/attendance"
                  className="text-white py-3 px-4 rounded-lg text-center text-sm font-medium hover:opacity-90 transition"
                  style={{backgroundColor: '#4caf50'}}
                >
                  ✅ Mark Attendance
                </Link>
                <Link
                  to="/leaves"
                  className="text-white py-3 px-4 rounded-lg text-center text-sm font-medium hover:opacity-90 transition"
                  style={{backgroundColor: '#ff9800'}}
                >
                  📝 Leave Requests
                </Link>
                <Link
                  to="/payroll"
                  className="text-white py-3 px-4 rounded-lg text-center text-sm font-medium hover:opacity-90 transition"
                  style={{backgroundColor: '#9c27b0'}}
                >
                  💰 Process Payroll
                </Link>
                <Link
                  to="/announcements"
                  className="text-white py-3 px-4 rounded-lg text-center text-sm font-medium hover:opacity-90 transition"
                  style={{backgroundColor: '#3f51b5'}}
                >
                  📢 Post Announcement
                </Link>
                <Link
                  to="/reports"
                  className="text-white py-3 px-4 rounded-lg text-center text-sm font-medium hover:opacity-90 transition"
                  style={{backgroundColor: '#607d8b'}}
                >
                  📊 View Reports
                </Link>
                {(user?.role === 'Admin' || user?.role === 'SuperAdmin') && (
                  <Link
                    to="/cms"
                    aria-label="Open Website CMS"
                    className="text-white py-3 px-4 rounded-lg text-center text-sm font-medium hover:opacity-90 transition"
                    style={{backgroundColor: '#0f766e'}}
                  >
                    🌐 Website CMS
                  </Link>
                )}
              </div>
            </div>

            {/* Recent Activities Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Recent Employees */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-lg font-bold mb-4 flex items-center" style={{color: '#2b3380'}}>
                  <span className="mr-2">👤</span> Recent Employees
                </h2>
                {stats.recent_employees && stats.recent_employees.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_employees.slice(0, 5).map((emp) => (
                      <Link 
                        key={emp.id}
                        to={`/employees/${emp.id}`}
                        className="block border-l-4 pl-3 py-2 hover:bg-gray-50 transition rounded"
                        style={{borderColor: '#2b3380'}}
                      >
                        <p className="font-medium text-sm">{emp.first_name} {emp.last_name}</p>
                        <p className="text-xs text-gray-600">{emp.designation}</p>
                        <p className="text-xs text-gray-500">{emp.employee_id}</p>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No recent employees</p>
                )}
              </div>

              {/* Recent Leave Requests */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-lg font-bold mb-4 flex items-center" style={{color: '#ff9800'}}>
                  <span className="mr-2">📝</span> Recent Leaves
                </h2>
                {stats.recent_leaves && stats.recent_leaves.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_leaves.slice(0, 5).map((leave) => (
                      <div 
                        key={leave.id} 
                        className="border-l-4 pl-3 py-2 rounded"
                        style={{borderColor: leave.status === 'Approved' ? '#4caf50' : leave.status === 'Rejected' ? '#f44336' : '#ff9800'}}
                      >
                        <p className="font-medium text-sm">{leave.employee_id}</p>
                        <p className="text-xs text-gray-600">{leave.leave_type}</p>
                        <p className="text-xs">
                          <span className={`inline-block px-2 py-0.5 rounded text-white text-xs ${
                            leave.status === 'Approved' ? 'bg-green-500' : 
                            leave.status === 'Rejected' ? 'bg-red-500' : 'bg-orange-500'
                          }`}>
                            {leave.status}
                          </span>
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No recent leave requests</p>
                )}
              </div>

              {/* Recent Announcements */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-lg font-bold mb-4 flex items-center" style={{color: '#3f51b5'}}>
                  <span className="mr-2">📢</span> Recent Announcements
                </h2>
                {stats.recent_announcements && stats.recent_announcements.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_announcements.map((announcement) => (
                      <div 
                        key={announcement.id} 
                        className="border-l-4 pl-3 py-2 rounded"
                        style={{borderColor: announcement.priority === 'High' ? '#f44336' : announcement.priority === 'Normal' ? '#ff9800' : '#4caf50'}}
                      >
                        <p className="font-medium text-sm">{announcement.title}</p>
                        <p className="text-xs text-gray-600 line-clamp-2">{announcement.content}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {announcement.priority} Priority
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No recent announcements</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-10">
            <p className="text-gray-600">No data available</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default DashboardEnhanced;
